import type { BossId, Settings } from '@bioforge/protocol';
import { Container, Graphics, Text } from 'pixi.js';
import { reducedMotion } from '../overlays/html.js';
import { idle } from '../ui/motion.js';
import { artSprite } from '../ui/textures.js';
import { LANDSCAPE, type FrameSize } from '../ui/frame.js';
import { COLOR, textStyle } from '../ui/theme.js';
import { UiButton, UiSlider, UiToggle, panel, speechBubble } from '../ui/widgets.js';

/** Диалоги боя по макетам: «Пауза» и итог уровня. Рисуются поверх размытого экрана. */

const caps = (text: string, color: number = COLOR.mint) =>
  new Text({
    text: text.toUpperCase(),
    style: textStyle({ size: 12, display: true, weight: '800', color, letterSpacing: 1.7 }),
  });

/** Плашки «Время / Очки / Босс» в ряд. */
function statTiles(stats: readonly (readonly [string, string])[], width: number): Container {
  const box = new Container();
  const gap = 8;
  const w = (width - gap * (stats.length - 1)) / stats.length;
  stats.forEach(([k, v], i) => {
    const tile = new Container();
    tile.x = i * (w + gap);
    tile.addChild(
      new Graphics()
        .roundRect(0, 0, w, 60, 12)
        .fill({ color: COLOR.night, alpha: 0.5 })
        .stroke({ width: 2, color: COLOR.borderDim }),
    );
    const key = new Text({ text: k, style: textStyle({ size: 12, color: COLOR.textMuted }) });
    key.position.set(12, 9);
    const value = new Text({
      text: v,
      style: textStyle({ size: 17, display: true, weight: '800' }),
    });
    value.position.set(12, 27);
    tile.addChild(key, value);
    box.addChild(tile);
  });
  return box;
}

/** Затемнение под диалогом на всё окно (с запасом за кадр). */
function shade(): Graphics {
  return new Graphics().rect(-4000, -4000, 9440, 9280).fill({ color: COLOR.night, alpha: 0.45 });
}

function dialogPanel(width: number, height: number): Graphics {
  const g = new Graphics();
  g.roundRect(-3, -3, width + 6, height + 6, 31).stroke({ width: 3, color: COLOR.border });
  g.roundRect(0, 26, width, height, 28).fill({ color: 0x000000, alpha: 0.5 });
  g.addChild(
    panel({
      width,
      height,
      radius: 28,
      top: 0x1e3e63,
      bottom: 0x0e1c3a,
      border: COLOR.ink,
      borderWidth: 3,
      alpha: 0.98,
    }),
  );
  return g;
}

export interface PauseOptions {
  /** «Глава 1 · Банка · уровень 5 — Спорница». */
  readonly subtitle: string;
  readonly stats: readonly (readonly [string, string])[];
  /** Босс уровня: реплика и подсказка внизу. */
  readonly boss: {
    readonly id: BossId;
    readonly name: string;
    readonly line: string;
    readonly hint: string;
  } | null;
  readonly settings: Settings;
  readonly onResume: () => void;
  readonly onRestart: () => void;
  readonly onLibrary: () => void;
  readonly onExit: () => void;
  /** Быстрые настройки изменены (сохраняет экран). */
  readonly onSettings: (settings: Settings) => void;
  /** Кадр экрана: на телефоне диалог складывается в одну колонку. */
  readonly frame?: FrameSize;
}

const PAUSE_W = 880;
const PAUSE_H = 500;
/** Портрет: колонки одна под другой. */
const PAUSE_TALL_W = 660;
const PAUSE_TALL_H = 960;

/** Пауза по макету «Пауза»: продолжить, заново, энциклопедия, выход; быстрые настройки. */
export class PauseDialog extends Container {
  private readonly stops: (() => void)[] = [];
  private settings: Settings;

  constructor(private readonly options: PauseOptions) {
    super();
    this.settings = options.settings;
    this.addChild(shade());
    const box = new Container();
    const frame = options.frame ?? LANDSCAPE;
    const tall = frame.width < frame.height;
    const dialogW = tall ? PAUSE_TALL_W : PAUSE_W;
    const dialogH = tall ? PAUSE_TALL_H : PAUSE_H;
    box.position.set((frame.width - dialogW) / 2, (frame.height - dialogH) / 2);
    box.addChild(dialogPanel(dialogW, dialogH));
    this.addChild(box);

    // Левая колонка: заголовок, статистика, действия.
    const leftW = tall ? dialogW - 68 : 430;
    const left = new Container();
    left.position.set(34, 30);
    const title = new Text({
      text: 'Пауза',
      style: textStyle({ size: 48, display: true, weight: '900', shadow: 4, lineHeight: 52 }),
    });
    const sub = new Text({
      text: options.subtitle,
      style: textStyle({ size: 15, color: COLOR.textSoft, wrap: leftW }),
    });
    sub.y = 62;
    const stats = statTiles(options.stats, leftW);
    stats.y = 96;
    const resume = new UiButton({
      width: leftW,
      height: 60,
      label: 'Продолжить',
      variant: 'primary',
      center: true,
      fontSize: 19,
      icon: { key: 'icon-play-ink', size: 20 },
      onTap: options.onResume,
    });
    resume.y = 172;
    left.addChild(title, sub, stats, resume);
    const actions: [string, string, () => void][] = [
      ['Начать уровень заново', 'icon-restart', options.onRestart],
      ['Энциклопедия', 'icon-book', options.onLibrary],
      ['Выйти к уровням', 'icon-exit', options.onExit],
    ];
    actions.forEach(([label, icon, onTap], i) => {
      const b = new UiButton({
        width: leftW,
        height: 50,
        radius: 14,
        label,
        fontSize: 16,
        icon: { key: icon, size: 20 },
        onTap,
      });
      b.y = 252 + i * 64;
      left.addChild(b);
    });
    box.addChild(left);

    // Правая колонка: быстрые настройки и подсказка босса.
    const rightW = tall ? leftW : PAUSE_W - 34 - leftW - 34 - 34;
    const right = new Container();
    right.position.set(tall ? 34 : 34 + leftW + 34, tall ? 30 + 444 + 20 : 30);
    right.addChild(caps('Быстрые настройки'));
    const sound = this.settings.sound;
    const sliders: [string, number, (v: number) => Settings['sound']][] = [
      ['Музыка', sound.music, (v) => ({ ...this.settings.sound, music: v })],
      ['Звуки', sound.effects, (v) => ({ ...this.settings.sound, effects: v })],
      ['Голоса существ', sound.voices, (v) => ({ ...this.settings.sound, voices: v })],
    ];
    sliders.forEach(([caption, value, apply], i) => {
      const slider = new UiSlider(caption, Math.round(value * 100), rightW, (percent) =>
        this.change({ ...this.settings, sound: apply(percent / 100) }),
      );
      slider.y = 32 + i * 62;
      right.addChild(slider);
    });
    const motion = new UiToggle(
      'Меньше анимации и тряски',
      reducedMotion(this.settings.accessibility.reducedMotion),
      rightW,
      (on) =>
        this.change({
          ...this.settings,
          accessibility: { ...this.settings.accessibility, reducedMotion: on ? 'on' : 'off' },
        }),
    );
    motion.y = 222;
    const hints = new UiToggle(
      'Карточки знакомства и подсказки',
      this.settings.accessibility.hints,
      rightW,
      (on) =>
        this.change({
          ...this.settings,
          accessibility: { ...this.settings.accessibility, hints: on },
        }),
    );
    hints.y = 268;
    right.addChild(motion, hints);

    const boss = options.boss;
    if (boss) {
      const tip = new Container();
      tip.y = tall ? 330 : PAUSE_H - 30 - 30 - 84;
      tip.addChild(
        new Graphics()
          .roundRect(0, 0, rightW, 84, 16)
          .fill({ color: 0x4a1638, alpha: 0.55 })
          .stroke({ width: 2, color: 0x5e2346 }),
      );
      const face = artSprite(`boss-${boss.id}`, 56);
      face.position.set(14 + 28, 42);
      const text = new Text({
        text: `Подсказка: ${boss.hint.charAt(0).toLowerCase()}${boss.hint.slice(1)}`,
        style: textStyle({ size: 14, color: 0xf2dce7, wrap: rightW - 96, lineHeight: 20 }),
      });
      text.anchor.set(0, 0.5);
      text.position.set(84, 42);
      tip.addChild(face, text);
      right.addChild(tip);

      // Босс дразнит из-за края диалога.
      const portrait = artSprite(`boss-${boss.id}`, 120);
      portrait.position.set(1100 + 60, 82 + 60);
      const bubble = speechBubble(boss.name, boss.line, 210);
      bubble.position.set(1100 - 20 - bubble.width, 70);
      // На телефоне за краем диалога места нет — босс дразнит только на широком экране.
      if (!tall) this.addChild(portrait, bubble);
      this.stops.push(idle(portrait, 'boss'), idle(bubble, 'talk'));
    }
    box.addChild(right);
  }

  private change(settings: Settings): void {
    this.settings = settings;
    this.options.onSettings(settings);
  }

  override destroy(): void {
    this.stops.splice(0).forEach((stop) => stop());
    super.destroy({ children: true });
  }
}

export interface ResultOptions {
  readonly won: boolean;
  /** Почему закончилась партия (у поражения). */
  readonly reason: string;
  readonly stats: readonly (readonly [string, string])[];
  /** Что с результатом на сервере: проверяется, засчитан, не принят. */
  readonly status: string;
  /** Отметки: «Новый рекорд уровня!», «Открыт уровень 1-3». */
  readonly marks: readonly string[];
  readonly busy: boolean;
  readonly onAgain: () => void;
  readonly onLevels: () => void;
  readonly frame?: FrameSize;
}

const RESULT_W = 560;

/** Итог уровня: победа или поражение, счёт, статус проверки сервером, повтор и возврат. */
export class ResultDialog extends Container {
  constructor(options: ResultOptions) {
    super();
    this.addChild(shade());
    const inner = RESULT_W - 68;
    const content = new Container();
    content.position.set(34, 30);
    const title = new Text({
      text: options.won ? 'Победа!' : 'Поражение',
      style: textStyle({
        size: 44,
        display: true,
        weight: '900',
        shadow: 4,
        lineHeight: 50,
        color: options.won ? COLOR.mint : COLOR.pink,
      }),
    });
    let y = 58;
    if (options.reason) {
      const reason = new Text({
        text: options.reason,
        style: textStyle({ size: 16, color: COLOR.textSoft }),
      });
      reason.y = y;
      content.addChild(reason);
      y += 30;
    }
    const stats = statTiles(options.stats, inner);
    stats.y = y + 6;
    y += 82;
    const status = new Text({
      text: options.status,
      style: textStyle({
        size: 15,
        color: options.busy ? COLOR.textMuted : COLOR.textSoft,
        wrap: inner,
      }),
    });
    status.y = y;
    y += status.height + 8;
    content.addChild(title, stats, status);
    for (const mark of options.marks) {
      const t = new Text({
        text: mark,
        style: textStyle({ size: 16, weight: '700', color: COLOR.gold }),
      });
      t.y = y;
      content.addChild(t);
      y += 26;
    }
    y += 12;
    const half = (inner - 12) / 2;
    const levels = new UiButton({
      width: half,
      height: 56,
      label: 'К уровням',
      variant: 'primary',
      center: true,
      fontSize: 17,
      onTap: options.onLevels,
    });
    const again = new UiButton({
      width: half,
      height: 56,
      label: 'Ещё раз',
      center: true,
      fontSize: 17,
      icon: { key: 'icon-restart', size: 20 },
      onTap: options.onAgain,
    });
    levels.y = y;
    again.position.set(half + 12, y);
    content.addChild(levels, again);
    const keys = new Text({
      text: 'Enter — к уровням · R — ещё раз',
      style: textStyle({ size: 13, color: COLOR.textMuted }),
    });
    keys.y = y + 70;
    content.addChild(keys);

    const height = y + 70 + 20 + 30;
    const box = new Container();
    const frame = options.frame ?? LANDSCAPE;
    box.position.set((frame.width - RESULT_W) / 2, (frame.height - height) / 2);
    box.addChild(dialogPanel(RESULT_W, height), content);
    this.addChild(box);
  }
}

export interface MatchResultOptions {
  readonly title: string;
  readonly won: boolean;
  readonly reason: string;
  /** Строки таблицы: имя, очки, лучшая цепочка, капсулы; `me` — строка игрока. */
  readonly rows: readonly {
    readonly name: string;
    readonly me: boolean;
    readonly score: number;
    readonly bestChain: number;
    readonly pieces: number;
  }[];
  /** Состояние повторного матча. */
  readonly rematch: 'offer' | 'waiting' | 'invited' | 'unavailable';
  readonly onRematch: () => void;
  readonly onMenu: () => void;
  readonly frame?: FrameSize;
}

const REMATCH_TEXT: Readonly<Record<MatchResultOptions['rematch'], string>> = {
  offer: 'Можно сыграть ещё раз в той же комнате.',
  waiting: 'Ждём согласия второго игрока…',
  invited: 'Второй игрок предлагает повторный матч!',
  unavailable: 'Второй игрок вышел — повторный матч недоступен.',
};

/** Результаты матча Versus и Co-op: итог, счёт обоих игроков, повторный матч и выход. */
export class MatchResultDialog extends Container {
  constructor(options: MatchResultOptions) {
    super();
    this.addChild(shade());
    const width = 640;
    const inner = width - 68;
    const content = new Container();
    content.position.set(34, 30);
    const title = new Text({
      text: options.title,
      style: textStyle({
        size: 44,
        display: true,
        weight: '900',
        shadow: 4,
        lineHeight: 50,
        color: options.won ? COLOR.mint : COLOR.pink,
      }),
    });
    const reason = new Text({
      text: options.reason,
      style: textStyle({ size: 16, color: COLOR.textSoft }),
    });
    reason.y = 58;
    content.addChild(title, reason);

    // Таблица: игрок, очки, цепочка, капсулы.
    const cols = [0, 270, 400, 500];
    let y = 100;
    ['Игрок', 'Очки', 'Цепочка', 'Капсулы'].forEach((label, i) => {
      const t = new Text({ text: label, style: textStyle({ size: 12, color: COLOR.textMuted }) });
      t.position.set((cols[i] ?? 0) + 14, y);
      content.addChild(t);
    });
    y += 22;
    for (const row of options.rows) {
      content.addChild(
        new Graphics()
          .roundRect(0, y, inner, 46, 12)
          .fill({ color: row.me ? 0x245678 : COLOR.night, alpha: row.me ? 0.7 : 0.5 })
          .stroke({ width: 2, color: row.me ? COLOR.mint : COLOR.borderDim }),
      );
      [
        row.name,
        row.score.toLocaleString('ru-RU'),
        `×${Math.max(1, row.bestChain)}`,
        String(row.pieces),
      ].forEach((value, i) => {
        const t = new Text({
          text: value,
          style: textStyle({
            size: i === 0 ? 15 : 16,
            display: i > 0,
            weight: i === 0 ? '700' : '800',
          }),
        });
        t.anchor.set(0, 0.5);
        t.position.set((cols[i] ?? 0) + 14, y + 23);
        content.addChild(t);
      });
      y += 54;
    }
    const status = new Text({
      text: REMATCH_TEXT[options.rematch],
      style: textStyle({
        size: 15,
        color: options.rematch === 'invited' ? COLOR.gold : COLOR.textSoft,
        weight: options.rematch === 'invited' ? '700' : '400',
      }),
    });
    status.y = y + 6;
    y += 40;
    const half = (inner - 12) / 2;
    const rematch = new UiButton({
      width: half,
      height: 56,
      label:
        options.rematch === 'invited'
          ? 'Принять реванш'
          : options.rematch === 'waiting'
            ? 'Ждём…'
            : 'Ещё раз',
      variant: 'primary',
      center: true,
      fontSize: 17,
      icon: { key: 'icon-restart', size: 20 },
      disabled: options.rematch === 'waiting' || options.rematch === 'unavailable',
      onTap: options.onRematch,
    });
    const menu = new UiButton({
      width: half,
      height: 56,
      label: 'В меню',
      center: true,
      fontSize: 17,
      icon: { key: 'icon-exit', size: 20 },
      onTap: options.onMenu,
    });
    rematch.y = y;
    menu.position.set(half + 12, y);
    const keys = new Text({
      text: 'Enter — ещё раз · Esc — в меню',
      style: textStyle({ size: 13, color: COLOR.textMuted }),
    });
    keys.y = y + 70;
    content.addChild(status, rematch, menu, keys);
    const height = y + 70 + 20 + 30;
    const box = new Container();
    const frame = options.frame ?? LANDSCAPE;
    box.position.set((frame.width - width) / 2, (frame.height - height) / 2);
    box.addChild(dialogPanel(width, height), content);
    this.addChild(box);
  }
}
